import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout,
  Card,
  Row,
  Col,
  Typography,
  Button,
  Table,
  Space,
  Statistic,
  DatePicker,
  Select,
  Modal,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  message,
  Tag,
  Spin,
  Empty,
  Progress,
  Divider,
  Checkbox,
  List,
  Tooltip,
  Radio,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  WalletOutlined,
  ThunderboltOutlined,
  HomeOutlined,
  TeamOutlined,
  ShoppingOutlined,
  ToolOutlined,
  WifiOutlined,
  SafetyOutlined,
  BulbOutlined,
  FileTextOutlined,
  AppstoreOutlined,
  PushpinOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { collection, getDocs, query, where } from 'firebase/firestore';
import API from '../config/api';
import { db } from '../firebaseConfig';
import {
  Expense,
  ExpenseCategory,
  ExpenseSummary,
  MonthlyTrendItem,
  FixedExpenseTemplate,
} from './type';
import DashboardSidebar from './DashboardSidebar';
import DashboardHeader from './DashboardHeader';
import { useLanguage } from './LanguageContext';
import { useClinicContext } from './ClinicContext';

const { Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const COPY_MODE_SEPARATE = 'separate';
const COPY_MODE_COPY = 'copy';

const CATEGORY_CONFIG: Record<ExpenseCategory, { icon: React.ReactNode; color: string }> = {
  electricity: { icon: <ThunderboltOutlined />, color: '#faad14' },
  water: { icon: <BulbOutlined />, color: '#1890ff' },
  rent: { icon: <HomeOutlined />, color: '#722ed1' },
  salary: { icon: <TeamOutlined />, color: '#eb2f96' },
  supplies: { icon: <ShoppingOutlined />, color: '#13c2c2' },
  equipment: { icon: <ToolOutlined />, color: '#fa8c16' },
  maintenance: { icon: <ToolOutlined />, color: '#a0d911' },
  internet: { icon: <WifiOutlined />, color: '#2f54eb' },
  insurance: { icon: <SafetyOutlined />, color: '#52c41a' },
  taxes: { icon: <FileTextOutlined />, color: '#f5222d' },
  marketing: { icon: <RiseOutlined />, color: '#ff7a45' },
  other: { icon: <AppstoreOutlined />, color: '#8c8c8c' },
};

const ExpensesManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [summary, setSummary] = useState<ExpenseSummary | null>(null);
  const [trend, setTrend] = useState<MonthlyTrendItem[]>([]);
  const [variableModalVisible, setVariableModalVisible] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('month');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  // Fixed expense state
  const [fixedModalVisible, setFixedModalVisible] = useState(false);
  const [templates, setTemplates] = useState<FixedExpenseTemplate[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [templateFormVisible, setTemplateFormVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<FixedExpenseTemplate | null>(null);
  const [applyingTemplates, setApplyingTemplates] = useState(false);

  const [form] = Form.useForm();
  const [templateForm] = Form.useForm();
  const navigate = useNavigate();
  const doctorId = localStorage.getItem('doctorId');
  const { clinics, selectedClinicId, selectedClinic, loading: clinicsLoading } = useClinicContext();
  const clinicId = selectedClinicId && selectedClinicId !== '__all_clinics__'
    ? selectedClinicId
    : clinics[0]?._id || '';
  const hasMultipleClinics = clinics.length > 1;
  const clinicOptions = clinics.map(clinic => ({
    value: clinic._id,
    label: clinic.name || clinic._id,
  }));
  const { t } = useLanguage();

  const getClinicLabel = (clinicId?: string) => (
    clinics.find(clinic => clinic._id === clinicId)?.name || clinicId || 'Selected clinic'
  );

  const getCategoryLabel = useCallback((cat: string): string => {
    const map: Record<string, string> = {
      electricity: t('catElectricity'),
      water: t('catWater'),
      rent: t('catRent'),
      salary: t('catSalary'),
      supplies: t('catSupplies'),
      equipment: t('catEquipment'),
      maintenance: t('catMaintenance'),
      internet: t('catInternet'),
      insurance: t('catInsurance'),
      taxes: t('catTaxes'),
      marketing: t('catMarketing'),
      other: t('catOther'),
    };
    return map[cat] || cat;
  }, [t]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('ar-EG', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const fetchData = useCallback(async () => {
    if (!doctorId) return;
    try {
      setLoading(true);
      const params = {
        startDate: dateRange[0]?.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        endDate: dateRange[1]?.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
        category: categoryFilter,
        ...(clinicId ? { clinic_id: clinicId } : {}),
      };

      const [expensesRes, summaryRes, trendRes] = await Promise.all([
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_EXPENSES(doctorId)}`, { params }),
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.EXPENSE_SUMMARY(doctorId)}`, { params }),
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.EXPENSE_TREND(doctorId)}`, { params: { months: 6 } }),
      ]);

      if (expensesRes.data.success) setExpenses(expensesRes.data.data);

      let nextSummary: ExpenseSummary | null = summaryRes.data.success ? summaryRes.data.data : null;
      let nextTrend: MonthlyTrendItem[] = trendRes.data.success ? trendRes.data.data : [];

      if (clinicId && nextSummary) {
        const dcaSnap = await getDocs(
          query(collection(db, 'doctor_clinic_assistant'), where('clinic_id', '==', clinicId))
        );
        const doctorIds = Array.from(
          new Set(dcaSnap.docs.map(d => d.data().doctor_id as string).filter(Boolean))
        );

        if (doctorIds.length > 0) {
          const revenueParams = {
            startDate: dateRange[0]?.startOf('day').format('YYYY-MM-DD'),
            endDate: dateRange[1]?.endOf('day').format('YYYY-MM-DD'),
            doctor_ids: doctorIds.join(','),
          };
          const clinicRevenueRes = await axios.get(
            `${API.BASE_URL}${API.ENDPOINTS.REVENUE_CLINIC(clinicId)}`,
            { params: revenueParams }
          );

          if (clinicRevenueRes.data?.success) {
            const clinicRevenue = clinicRevenueRes.data.data || {};
            const totalRevenue = Number(clinicRevenue.totalRevenue) || 0;
            const totalBillings = Number(clinicRevenue.billingCount) || 0;
            nextSummary = {
              ...nextSummary,
              totalRevenue,
              totalBillings,
              profit: totalRevenue - (Number(nextSummary.totalExpenses) || 0),
            };
          }

          if (nextTrend.length > 0) {
            const clinicTrend = await Promise.all(
              nextTrend.map(async (item) => {
                const monthStart = dayjs(`${item.year}-${String(item.month).padStart(2, '0')}-01`);
                const monthEnd = monthStart.endOf('month');
                const res = await axios.get(
                  `${API.BASE_URL}${API.ENDPOINTS.REVENUE_CLINIC(clinicId)}`,
                  {
                    params: {
                      startDate: monthStart.format('YYYY-MM-DD'),
                      endDate: monthEnd.format('YYYY-MM-DD'),
                      doctor_ids: doctorIds.join(','),
                    },
                  }
                );
                const data = res.data?.success ? (res.data.data || {}) : {};
                const revenue = Number(data.totalRevenue) || 0;
                const revenueCount = Number(data.billingCount) || 0;
                return {
                  ...item,
                  revenue,
                  revenueCount,
                  profit: revenue - (Number(item.expenses) || 0),
                };
              })
            );
            nextTrend = clinicTrend;
          }
        }
      }

      if (nextSummary) setSummary(nextSummary);
      setTrend(nextTrend);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  }, [doctorId, clinicId, dateRange, categoryFilter]);

  const fetchTemplates = useCallback(async () => {
    if (!doctorId) return;
    try {
      const res = await axios.get(
        `${API.BASE_URL}${API.ENDPOINTS.DOCTOR_FIXED_TEMPLATES(doctorId)}`,
        { params: clinicId ? { clinic_id: clinicId } : {} }
      );
      if (res.data.success) setTemplates(res.data.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, [doctorId, clinicId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handlePresetPeriod = (period: string) => {
    setActiveFilter(period);
    switch (period) {
      case 'today':
        setDateRange([dayjs().startOf('day'), dayjs().endOf('day')]);
        break;
      case 'week':
        setDateRange([dayjs().startOf('week'), dayjs().endOf('week')]);
        break;
      case 'month':
        setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
        break;
      case 'lastMonth':
        setDateRange([
          dayjs().subtract(1, 'month').startOf('month'),
          dayjs().subtract(1, 'month').endOf('month'),
        ]);
        break;
      case 'year':
        setDateRange([dayjs().startOf('year'), dayjs().endOf('year')]);
        break;
    }
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
      setActiveFilter('custom');
    }
  };

  // ─── Variable Expense Handlers ───

  const handleAddVariableExpense = () => {
    setEditingExpense(null);
    form.resetFields();
    form.setFieldsValue({ expenseDate: dayjs(), target_clinic_id: clinicId });
    setVariableModalVisible(true);
  };

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense);
    form.setFieldsValue({
      category: expense.category,
      title: expense.title,
      amount: expense.amount,
      notes: expense.notes,
      expenseDate: dayjs(expense.expenseDate),
      target_clinic_id: expense.clinic_id || clinicId,
    });
    setVariableModalVisible(true);
  };

  const handleSaveVariableExpense = async () => {
    try {
      const values = await form.validateFields();
      const targetClinicId = values.target_clinic_id || clinicId;
      const payload = {
        doctor_id: doctorId,
        clinic_id: targetClinicId,
        category: values.category,
        title: values.title,
        amount: values.amount,
        notes: values.notes || '',
        is_recurring: false,
        expenseDate: values.expenseDate?.toISOString(),
      };

      if (editingExpense) {
        const res = await axios.put(
          `${API.BASE_URL}${API.ENDPOINTS.EXPENSE(doctorId!, editingExpense.expense_id)}`,
          payload
        );
        if (res.data.success) message.success(t('expenseUpdated'));
      } else {
        const res = await axios.post(
          `${API.BASE_URL}${API.ENDPOINTS.EXPENSES}`,
          payload
        );
        if (res.data.success) message.success(t('expenseAdded'));
      }

      setVariableModalVisible(false);
      fetchData();
    } catch (error) {
      console.error('Error saving expense:', error);
      message.error(editingExpense ? t('expenseUpdateFailed') : t('expenseAddFailed'));
    }
  };

  const handleDeleteExpense = async (expenseId: string) => {
    try {
      const res = await axios.delete(
        `${API.BASE_URL}${API.ENDPOINTS.EXPENSE(doctorId!, expenseId)}`
      );
      if (res.data.success) {
        message.success(t('expenseDeleted'));
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
      message.error(t('expenseDeleteFailed'));
    }
  };

  // ─── Fixed Expense Handlers ───

  const handleOpenFixedModal = () => {
    setSelectedTemplateIds([]);
    setFixedModalVisible(true);
  };

  const handleSaveTemplate = async () => {
    try {
      const values = await templateForm.validateFields();
      const { target_clinic_id, data_mode, source_clinic_id, ...templateValues } = values;
      const targetClinicId = target_clinic_id || clinicId;
      const payload = {
        doctor_id: doctorId,
        clinic_id: targetClinicId,
        category: templateValues.category,
        title: templateValues.title,
        amount: templateValues.amount,
        notes: templateValues.notes || '',
      };

      if (editingTemplate) {
        const res = await axios.put(
          `${API.BASE_URL}${API.ENDPOINTS.FIXED_TEMPLATE(doctorId!, editingTemplate.template_id)}`,
          payload
        );
        if (res.data.success) message.success(t('templateUpdated'));
      } else {
        if (data_mode === COPY_MODE_COPY && source_clinic_id) {
          const sourceRes = await axios.get(
            `${API.BASE_URL}${API.ENDPOINTS.DOCTOR_FIXED_TEMPLATES(doctorId!)}`,
            { params: { clinic_id: source_clinic_id } }
          );
          const sourceTemplates = sourceRes.data.success ? (sourceRes.data.data || []) : [];
          await Promise.all(
            sourceTemplates.map((tmpl: FixedExpenseTemplate) => axios.post(
              `${API.BASE_URL}${API.ENDPOINTS.FIXED_EXPENSE_TEMPLATES}`,
              {
                doctor_id: doctorId,
                clinic_id: targetClinicId,
                category: tmpl.category,
                title: tmpl.title,
                amount: tmpl.amount,
                notes: tmpl.notes || '',
              }
            ))
          );
        }
        const res = await axios.post(
          `${API.BASE_URL}${API.ENDPOINTS.FIXED_EXPENSE_TEMPLATES}`,
          payload
        );
        if (res.data.success) message.success(t('templateAdded'));
      }

      setTemplateFormVisible(false);
      setEditingTemplate(null);
      templateForm.resetFields();
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      message.error(t('templateAddFailed'));
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      const res = await axios.delete(
        `${API.BASE_URL}${API.ENDPOINTS.FIXED_TEMPLATE(doctorId!, templateId)}`
      );
      if (res.data.success) {
        message.success(t('templateDeleted'));
        setSelectedTemplateIds(prev => prev.filter(id => id !== templateId));
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const handleEditTemplate = (tmpl: FixedExpenseTemplate) => {
    setEditingTemplate(tmpl);
    templateForm.setFieldsValue({
      category: tmpl.category,
      title: tmpl.title,
      amount: tmpl.amount,
      notes: tmpl.notes,
      target_clinic_id: clinicId,
      data_mode: COPY_MODE_SEPARATE,
    });
    setTemplateFormVisible(true);
  };

  const handleAddNewTemplate = () => {
    setEditingTemplate(null);
    templateForm.resetFields();
    templateForm.setFieldsValue({
      target_clinic_id: clinicId,
      data_mode: COPY_MODE_SEPARATE,
    });
    setTemplateFormVisible(true);
  };

  const handleApplySelectedTemplates = async () => {
    if (!selectedTemplateIds.length) return;
    try {
      setApplyingTemplates(true);
      const res = await axios.post(
        `${API.BASE_URL}${API.ENDPOINTS.FIXED_EXPENSES_APPLY}`,
        {
          doctor_id: doctorId,
          clinic_id: clinicId,
          template_ids: selectedTemplateIds,
          expenseDate: new Date().toISOString(),
        }
      );
      if (res.data.success) {
        message.success(`${res.data.data.length} ${t('templatesApplied')}`);
        setFixedModalVisible(false);
        setSelectedTemplateIds([]);
        fetchData();
      }
    } catch (error) {
      console.error('Error applying templates:', error);
      message.error(t('templatesApplyFailed'));
    } finally {
      setApplyingTemplates(false);
    }
  };

  const handleToggleTemplate = (templateId: string) => {
    setSelectedTemplateIds(prev =>
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTemplateIds.length === templates.length) {
      setSelectedTemplateIds([]);
    } else {
      setSelectedTemplateIds(templates.map(t => t.template_id));
    }
  };

  const selectedTotal = templates
    .filter(t => selectedTemplateIds.includes(t.template_id))
    .reduce((sum, t) => sum + t.amount, 0);

  const profitColor = (summary?.profit ?? 0) >= 0 ? '#52c41a' : '#f5222d';
  const maxCategoryAmount = Math.max(
    ...(summary?.categoryBreakdown?.map((c) => c.total) || [1])
  );

  const columns = [
    {
      title: t('expenseDate'),
      dataIndex: 'expenseDate',
      key: 'expenseDate',
      width: 120,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
      sorter: (a: Expense, b: Expense) =>
        new Date(a.expenseDate).getTime() - new Date(b.expenseDate).getTime(),
    },
    {
      title: t('expenseCategory'),
      dataIndex: 'category',
      key: 'category',
      width: 140,
      render: (cat: ExpenseCategory) => {
        const cfg = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.other;
        return (
          <Tag icon={cfg.icon} color={cfg.color}>
            {getCategoryLabel(cat)}
          </Tag>
        );
      },
    },
    {
      title: t('expenseTitle'),
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text: string, record: Expense) => (
        <Space>
          {record.is_recurring && (
            <Tooltip title={t('fixedExpenses')}>
              <PushpinOutlined style={{ color: '#722ed1' }} />
            </Tooltip>
          )}
          {text}
        </Space>
      ),
    },
    {
      title: t('expenseAmount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right' as const,
      render: (val: number) => (
        <Text strong style={{ color: '#f5222d' }}>
          {formatCurrency(val)} EGP
        </Text>
      ),
      sorter: (a: Expense, b: Expense) => a.amount - b.amount,
      defaultSortOrder: 'descend' as const,
    },
    {
      title: t('expenseNotes'),
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      width: 160,
      render: (text: string) => text || '-',
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 100,
      render: (_: any, record: Expense) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditExpense(record)}
            size="small"
          />
          <Popconfirm
            title={t('confirmDeleteExpense')}
            onConfirm={() => handleDeleteExpense(record.expense_id)}
            okText={t('delete')}
            cancelText={t('cancel')}
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const trendColumns = [
    {
      title: t('expenseDate'),
      dataIndex: 'label',
      key: 'label',
      width: 100,
    },
    {
      title: t('expenseTotalRevenue'),
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right' as const,
      render: (val: number) => (
        <Text style={{ color: '#52c41a' }}>{formatCurrency(val)} EGP</Text>
      ),
    },
    {
      title: t('expenseTotalExpenses'),
      dataIndex: 'expenses',
      key: 'expenses',
      align: 'right' as const,
      render: (val: number) => (
        <Text style={{ color: '#f5222d' }}>{formatCurrency(val)} EGP</Text>
      ),
    },
    {
      title: t('expenseProfit'),
      dataIndex: 'profit',
      key: 'profit',
      align: 'right' as const,
      render: (val: number) => (
        <Text strong style={{ color: val >= 0 ? '#52c41a' : '#f5222d' }}>
          {val >= 0 ? '+' : ''}{formatCurrency(val)} EGP
        </Text>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <DashboardSidebar collapsed={collapsed} setCollapsed={setCollapsed} />

      <Layout>
        <DashboardHeader
          onSettingsClick={() => navigate('/settings')}
          onMenuClick={() => {}}
          isMobile={false}
        />

        <Content style={{ margin: '24px 16px', padding: 24, background: '#f5f5f5' }}>
          {/* Header */}
          <Card style={{ marginBottom: 16 }}>
            <Row justify="space-between" align="middle" gutter={[8, 8]}>
              <Col>
                <Space>
                  <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard')}>
                    {t('backToDashboard')}
                  </Button>
                  <Title level={4} style={{ margin: 0 }}>
                    {t('expensesManagement')}
                  </Title>
                  {hasMultipleClinics && (
                    <Text type="secondary">
                      Current branch: <Text strong>{selectedClinic?.name || getClinicLabel(clinicId)}</Text>
                    </Text>
                  )}
                </Space>
              </Col>
              <Col>
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<PushpinOutlined />}
                    onClick={handleOpenFixedModal}
                    style={{ background: '#722ed1', borderColor: '#722ed1' }}
                  >
                    {t('addFixedExpense')}
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddVariableExpense}
                  >
                    {t('addVariableExpense')}
                  </Button>
                  <Button
                    type={activeFilter === 'month' ? 'primary' : 'default'}
                    onClick={() => handlePresetPeriod('month')}
                    ghost={activeFilter === 'month'}
                  >
                    {t('thisMonth')}
                  </Button>
                  <Button
                    type={activeFilter === 'lastMonth' ? 'primary' : 'default'}
                    onClick={() => handlePresetPeriod('lastMonth')}
                    ghost={activeFilter === 'lastMonth'}
                  >
                    {t('lastMonth')}
                  </Button>
                  <Button
                    type={activeFilter === 'year' ? 'primary' : 'default'}
                    onClick={() => handlePresetPeriod('year')}
                    ghost={activeFilter === 'year'}
                  >
                    This Year
                  </Button>
                  <RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    format="YYYY-MM-DD"
                  />
                  <Select
                    value={categoryFilter}
                    onChange={(val) => setCategoryFilter(val)}
                    style={{ width: 160 }}
                  >
                    <Select.Option value="all">{t('allCategories')}</Select.Option>
                    {Object.keys(CATEGORY_CONFIG).map((cat) => (
                      <Select.Option key={cat} value={cat}>
                        {getCategoryLabel(cat)}
                      </Select.Option>
                    ))}
                  </Select>
                  <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading} />
                </Space>
              </Col>
            </Row>
          </Card>

          <Spin spinning={loading}>
            {/* Summary Cards */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={8}>
                <Card style={{ borderTop: '4px solid #f5222d', height: '100%' }}>
                  <Statistic
                    title={<Text strong style={{ fontSize: 16 }}>{t('expenseTotalExpenses')}</Text>}
                    value={summary?.totalExpenses || 0}
                    suffix="EGP"
                    prefix={<WalletOutlined style={{ color: '#f5222d' }} />}
                    valueStyle={{ color: '#f5222d', fontSize: 28 }}
                  />
                  <Text type="secondary">
                    {summary?.expenseCount || 0} {t('expenses')}
                  </Text>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card style={{ borderTop: '4px solid #52c41a', height: '100%' }}>
                  <Statistic
                    title={<Text strong style={{ fontSize: 16 }}>{t('expenseTotalRevenue')}</Text>}
                    value={summary?.totalRevenue || 0}
                    suffix="EGP"
                    prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
                    valueStyle={{ color: '#52c41a', fontSize: 28 }}
                  />
                  <Text type="secondary">
                    {summary?.totalBillings || 0} bills
                  </Text>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card style={{ borderTop: `4px solid ${profitColor}`, height: '100%' }}>
                  <Statistic
                    title={<Text strong style={{ fontSize: 16 }}>{t('expenseProfit')}</Text>}
                    value={Math.abs(summary?.profit || 0)}
                    suffix="EGP"
                    prefix={
                      (summary?.profit ?? 0) >= 0
                        ? <RiseOutlined style={{ color: '#52c41a' }} />
                        : <FallOutlined style={{ color: '#f5222d' }} />
                    }
                    valueStyle={{ color: profitColor, fontSize: 28 }}
                  />
                  <Tag color={(summary?.profit ?? 0) >= 0 ? 'success' : 'error'}>
                    {(summary?.profit ?? 0) >= 0 ? t('profitPositive') : t('profitNegative')}
                  </Tag>
                </Card>
              </Col>
            </Row>

            {/* Revenue vs Expenses */}
            {summary && (summary.totalRevenue > 0 || summary.totalExpenses > 0) && (
              <Card title={t('expenseRevenueVsCost')} style={{ marginBottom: 16 }}>
                <Row gutter={24} align="middle">
                  <Col xs={24} md={12}>
                    <div style={{ marginBottom: 16 }}>
                      <Space style={{ marginBottom: 4 }}>
                        <DollarOutlined style={{ color: '#52c41a' }} />
                        <Text>{t('expenseTotalRevenue')}</Text>
                        <Text strong style={{ color: '#52c41a' }}>
                          {formatCurrency(summary.totalRevenue)} EGP
                        </Text>
                      </Space>
                      <Progress percent={100} strokeColor="#52c41a" showInfo={false} />
                    </div>
                    <div>
                      <Space style={{ marginBottom: 4 }}>
                        <WalletOutlined style={{ color: '#f5222d' }} />
                        <Text>{t('expenseTotalExpenses')}</Text>
                        <Text strong style={{ color: '#f5222d' }}>
                          {formatCurrency(summary.totalExpenses)} EGP
                        </Text>
                      </Space>
                      <Progress
                        percent={
                          summary.totalRevenue > 0
                            ? Math.min((summary.totalExpenses / summary.totalRevenue) * 100, 100)
                            : 100
                        }
                        strokeColor="#f5222d"
                        showInfo={false}
                      />
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    <div
                      style={{
                        textAlign: 'center',
                        padding: 24,
                        background: (summary.profit ?? 0) >= 0 ? '#f6ffed' : '#fff2f0',
                        borderRadius: 12,
                      }}
                    >
                      <Text type="secondary" style={{ fontSize: 14 }}>{t('expenseProfit')}</Text>
                      <Title level={2} style={{ margin: '8px 0 0', color: profitColor }}>
                        {(summary.profit ?? 0) >= 0 ? '+' : ''}{formatCurrency(summary.profit)} EGP
                      </Title>
                      {summary.totalRevenue > 0 && (
                        <Text type="secondary">
                          {((summary.totalExpenses / summary.totalRevenue) * 100).toFixed(1)}% of revenue goes to expenses
                        </Text>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card>
            )}

            {/* Category Breakdown & Monthly Trend */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={24} md={12}>
                <Card title={t('expenseCategoryBreakdown')} style={{ height: '100%' }}>
                  {summary?.categoryBreakdown && summary.categoryBreakdown.length > 0 ? (
                    <div>
                      {summary.categoryBreakdown.map((cat) => {
                        const cfg = CATEGORY_CONFIG[cat.category as ExpenseCategory] || CATEGORY_CONFIG.other;
                        const pct = maxCategoryAmount > 0 ? (cat.total / maxCategoryAmount) * 100 : 0;
                        return (
                          <div key={cat.category} style={{ marginBottom: 16 }}>
                            <Row justify="space-between" align="middle">
                              <Col>
                                <Space>
                                  <Tag icon={cfg.icon} color={cfg.color}>{getCategoryLabel(cat.category)}</Tag>
                                  <Text type="secondary">({cat.count})</Text>
                                </Space>
                              </Col>
                              <Col>
                                <Text strong style={{ color: '#f5222d' }}>{formatCurrency(cat.total)} EGP</Text>
                              </Col>
                            </Row>
                            <Progress percent={pct} strokeColor={cfg.color} showInfo={false} size="small" style={{ marginTop: 4 }} />
                          </div>
                        );
                      })}
                      <Divider />
                      <Row justify="space-between">
                        <Text strong>{t('total')}</Text>
                        <Text strong style={{ color: '#f5222d', fontSize: 18 }}>
                          {formatCurrency(summary.totalExpenses)} EGP
                        </Text>
                      </Row>
                    </div>
                  ) : (
                    <Empty description={t('noExpenses')} />
                  )}
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card title={t('expenseMonthlyTrend')} style={{ height: '100%' }}>
                  {trend.length > 0 ? (
                    <Table
                      dataSource={trend}
                      columns={trendColumns}
                      rowKey="label"
                      pagination={false}
                      size="small"
                      summary={(pageData) => {
                        const totalRev = pageData.reduce((s, i) => s + i.revenue, 0);
                        const totalExp = pageData.reduce((s, i) => s + i.expenses, 0);
                        const totalProfit = totalRev - totalExp;
                        return (
                          <Table.Summary.Row style={{ background: '#fafafa' }}>
                            <Table.Summary.Cell index={0}><Text strong>{t('total')}</Text></Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right">
                              <Text strong style={{ color: '#52c41a' }}>{formatCurrency(totalRev)}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} align="right">
                              <Text strong style={{ color: '#f5222d' }}>{formatCurrency(totalExp)}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} align="right">
                              <Text strong style={{ color: totalProfit >= 0 ? '#52c41a' : '#f5222d' }}>
                                {totalProfit >= 0 ? '+' : ''}{formatCurrency(totalProfit)}
                              </Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        );
                      }}
                    />
                  ) : (
                    <Empty description={t('noExpenses')} />
                  )}
                </Card>
              </Col>
            </Row>

            {/* Expenses Table */}
            <Card
              title={`${t('expenses')} (${expenses.length})`}
              extra={
                <Space>
                  <Button
                    icon={<PushpinOutlined />}
                    onClick={handleOpenFixedModal}
                    style={{ color: '#722ed1', borderColor: '#722ed1' }}
                  >
                    {t('addFixedExpense')}
                  </Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddVariableExpense}>
                    {t('addVariableExpense')}
                  </Button>
                </Space>
              }
            >
              <Table
                dataSource={expenses}
                columns={columns}
                rowKey="expense_id"
                pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (total) => `${total}` }}
                scroll={{ x: 800 }}
                locale={{ emptyText: <Empty description={t('noExpenses')} /> }}
              />
            </Card>
          </Spin>

          {/* ─── Variable Expense Modal ─── */}
          <Modal
            title={editingExpense ? t('editExpense') : t('addVariableExpense')}
            open={variableModalVisible}
            onOk={handleSaveVariableExpense}
            onCancel={() => setVariableModalVisible(false)}
            okText={t('save')}
            cancelText={t('cancel')}
            width={520}
            destroyOnClose
          >
            <Form form={form} layout="vertical">
              {hasMultipleClinics && (
                <Form.Item
                  name="target_clinic_id"
                  label="Branch / الفرع"
                  rules={[{ required: true, message: 'Please select the branch' }]}
                >
                  <Select
                    loading={clinicsLoading}
                    options={clinicOptions}
                    placeholder="Select branch"
                  />
                </Form.Item>
              )}
              <Form.Item
                name="category"
                label={t('expenseCategory')}
                rules={[{ required: true, message: t('expenseCategory') }]}
              >
                <Select placeholder={t('expenseCategory')}>
                  {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                    <Select.Option key={key} value={key}>
                      <Space>{cfg.icon}{getCategoryLabel(key)}</Space>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="title"
                label={t('expenseTitle')}
                rules={[{ required: true, message: t('expenseTitle') }]}
              >
                <Input placeholder={t('expenseTitle')} />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="amount"
                    label={t('expenseAmount')}
                    rules={[{ required: true, message: t('expenseAmount') }]}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} placeholder="0" addonAfter="EGP" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="expenseDate"
                    label={t('expenseDate')}
                    rules={[{ required: true, message: t('expenseDate') }]}
                  >
                    <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="notes" label={t('expenseNotes')}>
                <Input.TextArea rows={2} placeholder={t('expenseNotes')} />
              </Form.Item>
            </Form>
          </Modal>

          {/* ─── Fixed Expenses Modal ─── */}
          <Modal
            title={
              <Space>
                <PushpinOutlined style={{ color: '#722ed1' }} />
                {t('fixedExpenseTemplates')}
              </Space>
            }
            open={fixedModalVisible}
            onCancel={() => setFixedModalVisible(false)}
            width={640}
            footer={
              <Row justify="space-between" align="middle">
                <Col>
                  {selectedTemplateIds.length > 0 && (
                    <Text strong style={{ color: '#722ed1' }}>
                      {t('fixedExpenseTotal')}: {formatCurrency(selectedTotal)} EGP
                    </Text>
                  )}
                </Col>
                <Col>
                  <Space>
                    <Button onClick={() => setFixedModalVisible(false)}>{t('cancel')}</Button>
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      onClick={handleApplySelectedTemplates}
                      disabled={selectedTemplateIds.length === 0}
                      loading={applyingTemplates}
                      style={{ background: '#722ed1', borderColor: '#722ed1' }}
                    >
                      {t('applyFixedExpenses')} ({selectedTemplateIds.length})
                    </Button>
                  </Space>
                </Col>
              </Row>
            }
            destroyOnClose
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              {t('selectTemplatesToApply')}
            </Text>

            {templates.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <Checkbox
                  checked={selectedTemplateIds.length === templates.length}
                  indeterminate={selectedTemplateIds.length > 0 && selectedTemplateIds.length < templates.length}
                  onChange={handleSelectAll}
                >
                  <Text strong>
                    {selectedTemplateIds.length === templates.length ? t('cancel') : `${t('allCategories')}`}
                  </Text>
                </Checkbox>
              </div>
            )}

            {templates.length > 0 ? (
              <List
                dataSource={templates}
                renderItem={(tmpl) => {
                  const cfg = CATEGORY_CONFIG[tmpl.category] || CATEGORY_CONFIG.other;
                  const isSelected = selectedTemplateIds.includes(tmpl.template_id);
                  return (
                    <List.Item
                      style={{
                        padding: '12px 16px',
                        marginBottom: 8,
                        borderRadius: 8,
                        border: isSelected ? '2px solid #722ed1' : '1px solid #f0f0f0',
                        background: isSelected ? '#f9f0ff' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onClick={() => handleToggleTemplate(tmpl.template_id)}
                      actions={[
                        <Button
                          key="edit"
                          type="link"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={(e) => { e.stopPropagation(); handleEditTemplate(tmpl); }}
                        />,
                        <Popconfirm
                          key="delete"
                          title={t('confirmDeleteTemplate')}
                          onConfirm={(e) => { e?.stopPropagation(); handleDeleteTemplate(tmpl.template_id); }}
                          onCancel={(e) => e?.stopPropagation()}
                          okText={t('delete')}
                          cancelText={t('cancel')}
                        >
                          <Button
                            type="link"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Popconfirm>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={
                          <Checkbox
                            checked={isSelected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => handleToggleTemplate(tmpl.template_id)}
                          />
                        }
                        title={
                          <Space>
                            <Tag icon={cfg.icon} color={cfg.color}>{getCategoryLabel(tmpl.category)}</Tag>
                            <Text strong>{tmpl.title}</Text>
                          </Space>
                        }
                        description={tmpl.notes || undefined}
                      />
                      <Text strong style={{ color: '#f5222d', fontSize: 16, whiteSpace: 'nowrap' }}>
                        {formatCurrency(tmpl.amount)} EGP
                      </Text>
                    </List.Item>
                  );
                }}
              />
            ) : (
              <Empty description={t('noTemplates')} style={{ margin: '24px 0' }} />
            )}

            <Divider />
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddNewTemplate}
              block
            >
              {t('addNewTemplate')}
            </Button>
          </Modal>

          {/* ─── Template Create/Edit Modal ─── */}
          <Modal
            title={editingTemplate ? t('editTemplate') : t('addNewTemplate')}
            open={templateFormVisible}
            onOk={handleSaveTemplate}
            onCancel={() => { setTemplateFormVisible(false); setEditingTemplate(null); }}
            okText={t('save')}
            cancelText={t('cancel')}
            width={480}
            destroyOnClose
          >
            <Form form={templateForm} layout="vertical">
              {hasMultipleClinics && (
                <>
                  <Form.Item
                    name="target_clinic_id"
                    label="Branch / الفرع"
                    rules={[{ required: true, message: 'Please select the branch' }]}
                  >
                    <Select
                      loading={clinicsLoading}
                      options={clinicOptions}
                      placeholder="Select branch"
                    />
                  </Form.Item>
                  {!editingTemplate && (
                    <>
                      <Form.Item
                        name="data_mode"
                        label="Data setup / طريقة الإضافة"
                      >
                        <Radio.Group>
                          <Radio.Button value={COPY_MODE_SEPARATE}>Set separately</Radio.Button>
                          <Radio.Button value={COPY_MODE_COPY}>Copy from another clinic</Radio.Button>
                        </Radio.Group>
                      </Form.Item>
                      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.data_mode !== cur.data_mode || prev.target_clinic_id !== cur.target_clinic_id}>
                        {({ getFieldValue }) => getFieldValue('data_mode') === COPY_MODE_COPY ? (
                          <Form.Item
                            name="source_clinic_id"
                            label="Copy existing templates from / نسخ البيانات من"
                            rules={[{ required: true, message: 'Please select source clinic' }]}
                          >
                            <Select
                              loading={clinicsLoading}
                              options={clinicOptions.filter(option => option.value !== getFieldValue('target_clinic_id'))}
                              placeholder="Select source clinic"
                            />
                          </Form.Item>
                        ) : null}
                      </Form.Item>
                    </>
                  )}
                </>
              )}
              <Form.Item
                name="category"
                label={t('expenseCategory')}
                rules={[{ required: true, message: t('expenseCategory') }]}
              >
                <Select placeholder={t('expenseCategory')}>
                  {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                    <Select.Option key={key} value={key}>
                      <Space>{cfg.icon}{getCategoryLabel(key)}</Space>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="title"
                label={t('expenseTitle')}
                rules={[{ required: true, message: t('expenseTitle') }]}
              >
                <Input placeholder={t('expenseTitle')} />
              </Form.Item>
              <Form.Item
                name="amount"
                label={t('expenseAmount')}
                rules={[{ required: true, message: t('expenseAmount') }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" addonAfter="EGP" />
              </Form.Item>
              <Form.Item name="notes" label={t('expenseNotes')}>
                <Input.TextArea rows={2} placeholder={t('expenseNotes')} />
              </Form.Item>
            </Form>
          </Modal>
        </Content>
      </Layout>
    </Layout>
  );
};

export default ExpensesManagement;
